import * as Dialog from '@radix-ui/react-dialog';
import { useRef } from 'react';

import { i18n, type AppLanguage } from '../../i18n';
import './language.css';

export interface FirstRunLanguageDialogProps {
  readonly open: boolean;
  readonly suggestedLanguage: AppLanguage;
  readonly busy?: boolean;
  readonly onSelect: (language: AppLanguage) => void | Promise<void>;
}

export function FirstRunLanguageDialog({
  open,
  suggestedLanguage,
  busy = false,
  onSelect,
}: FirstRunLanguageDialogProps) {
  const englishButtonRef = useRef<HTMLButtonElement>(null);
  const arabicButtonRef = useRef<HTMLButtonElement>(null);
  const english = i18n.getFixedT('en');
  const arabic = i18n.getFixedT('ar');

  const focusSuggestedLanguage = () => {
    const button = suggestedLanguage === 'ar' ? arabicButtonRef.current : englishButtonRef.current;
    button?.focus();
  };

  return (
    <Dialog.Root open={open} onOpenChange={() => undefined}>
      <Dialog.Portal>
        <Dialog.Overlay className="language-dialog__overlay" />
        <Dialog.Content
          className="language-dialog__content"
          aria-busy={busy}
          onEscapeKeyDown={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            focusSuggestedLanguage();
          }}
          onPointerDownOutside={(event) => event.preventDefault()}
        >
          <div className="language-dialog__brand" aria-label={english('app.name')}>
            <span aria-hidden="true" className="language-dialog__brand-mark">
              S
            </span>
            <span dir="ltr">{english('app.name')}</span>
          </div>

          <Dialog.Title className="language-dialog__title">
            <span lang="en" dir="ltr">
              {english('language.firstRun.headingEnglish')}
            </span>
            <span lang="ar" dir="rtl">
              {arabic('language.firstRun.headingArabic')}
            </span>
          </Dialog.Title>

          <Dialog.Description className="language-dialog__description">
            <span lang="en" dir="ltr">
              {english('language.firstRun.descriptionEnglish')}
            </span>
            <span lang="ar" dir="rtl">
              {arabic('language.firstRun.descriptionArabic')}
            </span>
          </Dialog.Description>

          <div className="language-dialog__choices">
            <button
              ref={englishButtonRef}
              className="language-dialog__choice"
              type="button"
              lang="en"
              dir="ltr"
              aria-label={english('language.firstRun.useEnglish')}
              disabled={busy}
              onClick={() => void onSelect('en')}
            >
              {english('language.names.english')}
            </button>
            <button
              ref={arabicButtonRef}
              className="language-dialog__choice"
              type="button"
              lang="ar"
              dir="rtl"
              aria-label={arabic('language.firstRun.useArabic')}
              disabled={busy}
              onClick={() => void onSelect('ar')}
            >
              {arabic('language.names.arabic')}
            </button>
          </div>

          <p className="language-dialog__privacy">
            <span lang="en" dir="ltr">
              {english('language.firstRun.privacyEnglish')}
            </span>
            <span aria-hidden="true">•</span>
            <span lang="ar" dir="rtl">
              {arabic('language.firstRun.privacyArabic')}
            </span>
          </p>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
